import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid2 } from '@mui/material';
import { fetchApiDefinitionById, currentApiDefinitionSelector } from '../store/apiDetails';
import { availableTabsSelector } from '../store/apiDetails/selectors';
import { useLocalizedFeatures } from '../hooks/useLocalizedContent';
import { getLocalizedContent } from '../utils/apiUtils';
import { Language, ApiDetailTab } from '../types/app';
import { AppDispatch, RootState } from '../store/store';
import { LoadingSpinner, ErrorDisplay, ProjectCard } from '../components/common';
import ApiHeader from '../components/common/ApiHeader/ApiHeader';
import MarkdownRenderer from '../components/ApiDetail/GuidesTab/MarkdownRenderer';
import { markdownContentSx } from '@/components/ApiDetail/GuidesTab/ArticleViewer.styles';
import { StyledFeatureContentBox } from './ApiFeatureDemo.styles';

const ApiFeatureDemo: React.FC = () => {
  const { slug, featureSlug } = useParams<{ slug: string; featureSlug: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { i18n, t } = useTranslation();

  const currentApiDefinition = useSelector((state: RootState) =>
    currentApiDefinitionSelector(state, slug),
  );
  const availableTabs = useSelector((state: RootState) => availableTabsSelector(state, slug));

  const features = useLocalizedFeatures(currentApiDefinition);
  const selectedLanguage = i18n.language as Language;

  const currentFeature = features.find((feature) => feature.slug === featureSlug);
  useEffect(() => {
    if (slug && !currentApiDefinition) {
      dispatch(fetchApiDefinitionById(slug));
    }
  }, [dispatch, slug, currentApiDefinition]);

  const handleBackClick = () => {
    navigate(`/api-definitions/${slug}`);
  };

  const handleTabChange = (tab: string) => {
    if (tab === ApiDetailTab.FEATURES) {
      return;
    }
    navigate(`/api-definitions/${slug}?tab=${tab}`);
  };

  if (!currentApiDefinition) {
    return <LoadingSpinner />;
  }

  if (!currentFeature) {
    return (
      <Container>
        <ErrorDisplay error={t('featureDemo.featureNotFound')} />
      </Container>
    );
  }

  const localizedContent = getLocalizedContent(currentApiDefinition, selectedLanguage);
  const apiTitle = localizedContent?.title || t('apiDetail.fallbackTitle');

  const apiHeaderData = {
    title: `${apiTitle} » ${currentFeature?.name || ''}`,
    isPublic: currentApiDefinition?.isPublic || false,
    tags: localizedContent?.tags?.map((tag) => tag.name) || [],
  };

  return (
    <Box sx={{ p: 3 }}>
      <ApiHeader
        apiHeaderData={apiHeaderData}
        activeTab={ApiDetailTab.FEATURES}
        onTabChange={handleTabChange}
        onBackClick={handleBackClick}
        tabs={availableTabs}
      />

      <Grid2 container spacing={3} sx={{ mt: 3 }}>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <StyledFeatureContentBox sx={markdownContentSx}>
            <MarkdownRenderer content={currentFeature.demoContent || ''} />
          </StyledFeatureContentBox>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <ProjectCard apiDefinition={currentApiDefinition} />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ApiFeatureDemo;
